import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { AdTouchpointSourceType } from '@trello/ad-sequencer';
import {
  type TouchpointSourceType,
  useAdSequencer,
} from '@trello/ad-sequencer';
import { isDesktop } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureFlag } from '@trello/feature-flag-client';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import {
  featureTouchpoints,
  isSourceTargetedByFeatureKey,
} from './isSourceTargetedByFeatureFlag';
const experimentFeatureKey = 'trello_xf_ad_sequencer_internal_experiment';

const oneTimeMessageSources: Partial<Record<TouchpointSourceType, string>> = {
  cardBackDatePicker: '1p-discovery-ad-card-date-range-picker',
  createBoardInlineDialog: '1p-discovery-ad-left-hand-nav-board-create',
  memberHomeWorkspaceSidebarSection: '1p-discovery-ad-left-hand-nav-board-home',
  boardDirectory: '1p-discovery-ad-powerup-directory',
};

export type CrossFlowForAdSequencerInternalExperimentVariations =
  ExperimentVariations<typeof experimentFeatureKey, 'cohort'>;

export interface CrossFlowForAdSequencerInternalExperimentParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
}

export const useCrossFlowForAdSequencerInternalExperiment = ({
  source,
  workspaceId,
  boardId,
}: CrossFlowForAdSequencerInternalExperimentParams): TrelloCrossFlowExperiment<CrossFlowForAdSequencerInternalExperimentVariations> => {
  const crossFlow = useCrossFlow();

  const [hasValidAaSession] = useHasValidAaSession();

  const [fireExposureEvent, setFireExposureEvent] = useState(false);

  const { cohort } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'cohort',
    fireExposureEvent,
  });

  const { rotationInterval } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'rotationInterval',
    fireExposureEvent: false,
  });

  const [nextAdSource, setNextAdSource] = useState<AdTouchpointSourceType>();

  const { getNextAd } = useAdSequencer({
    experience: experimentFeatureKey,
    adTouchpointSources: featureTouchpoints[experimentFeatureKey],
    adRotationIntervalInMinutes: !Number.isNaN(Number(rotationInterval))
      ? Number(rotationInterval)
      : 0,
  });

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const isSourceOneTimeMessageDismissed = useCallback(
    () => isOneTimeMessageDismissed(oneTimeMessageSources?.[source] || ''),
    [source, isOneTimeMessageDismissed],
  );

  const isInternalAtlassian = useFeatureFlag('atlassian-team', false);

  const isRuntimeEligibilityChecksPassed = useMemo(() => {
    return (
      !isDesktop() &&
      cohort !== 'not-enrolled' &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      }) &&
      !isSourceOneTimeMessageDismissed() &&
      isInternalAtlassian &&
      source === nextAdSource
    );
  }, [
    cohort,
    source,
    isSourceOneTimeMessageDismissed,
    isInternalAtlassian,
    nextAdSource,
  ]);

  const getIsTargeted = useCallback(() => {
    return cohort !== 'not-enrolled';
  }, [cohort]);

  const getExperimentCohort = useCallback(() => {
    if (isRuntimeEligibilityChecksPassed) {
      setFireExposureEvent(true);
      return cohort;
    }
    return 'not-enrolled';
  }, [cohort, isRuntimeEligibilityChecksPassed]);

  useEffect(() => {
    const loadNextAd = async () => {
      if (cohort !== 'not-enrolled') {
        if (cohort === 'experiment') {
          setNextAdSource(await getNextAd());
          return;
        } else {
          setNextAdSource(source);
          return;
        }
      }
    };

    loadNextAd();
  }, [cohort, getNextAd, source]);

  return useMemo(
    () => ({
      isEnrolled: isRuntimeEligibilityChecksPassed,
      experimentName: experimentFeatureKey,
      getIsTargeted,
      getExperimentCohort,
      open: trelloCrossFlowOpen(crossFlow, source, [], hasValidAaSession, {
        workspaceId,
        boardId,
      }),
    }),
    [
      boardId,
      crossFlow,
      getExperimentCohort,
      getIsTargeted,
      hasValidAaSession,
      isRuntimeEligibilityChecksPassed,
      source,
      workspaceId,
    ],
  );
};
